import React, { createContext, useContext, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { createIntegrationsClient } from './clients/apolloClients';

// Create context for Integrations Apollo client
const IntegrationsClientContext = createContext(null);

// Custom hook to access the Integrations client
export const useIntegrationsClient = () => {
  const client = useContext(IntegrationsClientContext);
  if (!client) {
    throw new Error('useIntegrationsClient must be used within ApolloProvider');
  }
  return client;
};

/**
 * Apollo Provider that creates and provides the Integrations Apollo Client
 * This wraps the app and makes the client available via context
 */
export const ApolloProvider = ({ children }) => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  // Create Apollo client with memoization to prevent recreation on every render
  const integrationsClient = useMemo(() => {
    if (!isAuthenticated) return null;
    return createIntegrationsClient(getAccessTokenSilently);
  }, [getAccessTokenSilently, isAuthenticated]);

  // Show loading state while client is being created
  if (!isAuthenticated || !integrationsClient) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p className="mt-2">Initializing GraphQL client...</p>
        </div>
      </div>
    );
  }

  return (
    <IntegrationsClientContext.Provider value={integrationsClient}>
      {children}
    </IntegrationsClientContext.Provider>
  );
};
